import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

/**
 * NumberField — number input that formats ONLY on blur (LAT-11).
 *
 * The bug this fixes: every prior implementation reformatted the value on
 * each keystroke, which (a) reset the caret, and (b) made it impossible
 * to type a single-digit price like "5" without it becoming "0.05" or "$5.00"
 * mid-stroke.
 *
 * Behaviour:
 *   - While focused: keep RAW typed string in state. No formatting, no
 *     mutation. The user types "5" and sees "5".
 *   - On blur: parse the raw string → number → format → emit numeric value
 *     to the parent (`onChange(numericValue)`).
 *   - If the raw string is empty/invalid on blur, emit null.
 *   - Programmatic value changes (controlled `value` prop) are reflected
 *     when NOT focused; we never overwrite mid-edit.
 *
 * Display formats:
 *   - kind="currency"  →  "$1,234.50"   (USD by default)
 *   - kind="percent"   →  "12.5%"       (value is the literal number, not /100)
 *   - kind="decimal"   →  "1,234.5"
 *   - kind="integer"   →  "1,234"
 *
 * Props:
 *   value           number | null            (controlled)
 *   onChange(num)   parsed numeric or null
 *   kind            'currency'|'percent'|'decimal'|'integer'   default 'decimal'
 *   currency        ISO code, default 'USD'
 *   decimals        max fractional digits (kind defaults below)
 *   min, max        clamped on blur
 *   prefix, suffix  visual adornments inside the field
 *   …rest           spread to the underlying <input>
 */

const KIND_DEFAULTS = {
  currency: { decimals: 2, prefix: '$' },
  percent:  { decimals: 2, suffix: '%' },
  decimal:  { decimals: 2 },
  integer:  { decimals: 0 },
};

function formatNumber(num, { kind, decimals, currency = 'USD' }) {
  if (num === null || num === undefined || Number.isNaN(num)) return '';
  if (kind === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  }
  if (kind === 'integer') {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
      Math.round(num)
    );
  }
  // decimal & percent share the same numeric body
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Strip everything that isn't a digit, sign, or decimal point — tolerant of
 * the user typing $, commas, spaces, %.
 */
function parseLoose(str) {
  if (str === null || str === undefined) return null;
  const trimmed = String(str).trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '.') return null;
  const cleaned = trimmed.replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function clamp(n, min, max) {
  if (n === null || n === undefined) return n;
  if (typeof min === 'number' && n < min) return min;
  if (typeof max === 'number' && n > max) return max;
  return n;
}

const NumberField = React.forwardRef(function NumberField(
  {
    value,
    onChange,
    kind = 'decimal',
    currency = 'USD',
    decimals,
    min,
    max,
    prefix,
    suffix,
    className,
    inputClassName,
    disabled,
    readOnly,
    placeholder,
    id,
    name,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  forwardedRef
) {
  const defaults = KIND_DEFAULTS[kind] || KIND_DEFAULTS.decimal;
  const finalDecimals = decimals ?? defaults.decimals;
  const finalPrefix = prefix ?? defaults.prefix;
  const finalSuffix = suffix ?? defaults.suffix;

  const [focused, setFocused] = useState(false);
  // The raw, in-progress string the user is typing.
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  // Merge the internal ref with any forwarded ref so callers can hook this
  // input up directly to react-hook-form's `register()`.
  const setRef = (node) => {
    inputRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  // The text rendered in the input:
  //   - while focused → whatever the user has typed (preserve caret)
  //   - while blurred → formatted version of the prop value
  const displayValue = focused
    ? draft
    : formatNumber(value ?? null, { kind, decimals: finalDecimals, currency });

  // Keep draft in sync if the parent updates value while the field is NOT
  // focused (e.g. form reset, async load). We never overwrite during edit.
  useEffect(() => {
    if (!focused) {
      setDraft(value === null || value === undefined || Number.isNaN(value)
        ? ''
        : String(value));
    }
  }, [value, focused]);

  return (
    <div
      className={clsx(
        'relative inline-flex items-center w-full',
        className
      )}
    >
      {finalPrefix && (
        <span
          className="pointer-events-none absolute left-3 select-none text-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {finalPrefix}
        </span>
      )}
      <input
        ref={setRef}
        id={id}
        name={name}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        value={displayValue}
        onFocus={(e) => {
          // On focus, switch to raw editing. Show the bare numeric string
          // (no $, no commas) so the user can edit cleanly. Select-all is a
          // friendly default for currency/measurement fields.
          setFocused(true);
          setDraft(
            value === null || value === undefined || Number.isNaN(value)
              ? ''
              : String(value)
          );
          // defer select so it runs after the new value paints
          requestAnimationFrame(() => e.target?.select?.());
        }}
        onChange={(e) => {
          // Keep the raw string verbatim — no parsing, no reformatting.
          setDraft(e.target.value);
        }}
        onBlur={() => {
          setFocused(false);
          const parsed = parseLoose(draft);
          const clamped = clamp(parsed, min, max);
          // Only emit if the parsed value differs from the prop, to avoid
          // spurious onChange callbacks on re-blur.
          if (clamped !== (value ?? null)) {
            onChange?.(clamped);
          }
        }}
        className={clsx(
          'w-full text-sm focus:outline-none focus-visible:ring-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          inputClassName
        )}
        style={{
          height: 'var(--input-h)',
          paddingLeft: finalPrefix ? '1.75rem' : 'var(--input-pad-x, 0.75rem)',
          paddingRight: finalSuffix ? '1.75rem' : 'var(--input-pad-x, 0.75rem)',
          paddingTop: 'var(--input-pad-y)',
          paddingBottom: 'var(--input-pad-y)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-default)',
          textAlign: 'right',
        }}
        {...rest}
      />
      {finalSuffix && (
        <span
          className="pointer-events-none absolute right-3 select-none text-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {finalSuffix}
        </span>
      )}
    </div>
  );
});

NumberField.displayName = 'NumberField';
export default NumberField;
